import { Component, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { ProjectService } from 'src/app/services/project.service';
import { UserService } from 'src/app/services/user.service';
import { Project } from '../../models/project.model';

@Component({
    selector: 'app-create-project',
    templateUrl: './create-project.component.html',
    styleUrl: './create-project.component.scss',
    standalone: false
})
export class CreateProjectComponent {
	@ViewChild('createProject') createProjectModal!: TemplateRef<any>;
	@ViewChild('successModal') successModal!: TemplateRef<any>;
	@ViewChild('errorModal') errorModal!: TemplateRef<any>;

	currentStep = 0;
  steps = ['Project Details', 'Team Members', 'Summary'];
	loading = true;
	today = Date.now();

	projects: Project[] = [];
	users: any[] = [];
	selectedUsers: any[] = [];
	private searchSubject = new Subject<string>();

	createProjectModalRef: any;
	createForm!: UntypedFormGroup;
	selectedProject: Project | null = null;
	description: string | null = null;

	currentPage: number = 1;
  pageSize: number = 8;
  totalUsers: number = 0;
  searchText: string = '';
  sortColumn: string = 'createdAt';
  sortDirection: string = 'desc';

	startIndex: number = 0;
  endIndex: number = 0;

	submitted = false;
	spinner = false;
	successMessage = '';
	success = false;

	constructor(private modalService: NgbModal, private router: Router, private formBuilder: UntypedFormBuilder, private authService: AuthService, private userService: UserService, private projectService: ProjectService) {}	

	get f() { return this.createForm.controls; }

	openModal() {
		this.success = false;
		this.submitted = false;
		this.spinner = false;
		this.selectedUsers = [];

		this.selectedProject = null;
		this.currentStep = 0;
		this.subscribeSearchText();

		this.createForm = this.formBuilder.group({
			name: ['', [Validators.required]]
		});

		if (this.authService.currentClientValue.plan) {
			this.createProjectModalRef = this.modalService.open(this.createProjectModal, { size: 'xl', backdrop: 'static', keyboard : false });
		} else {
			this.createProjectModalRef = this.modalService.open(this.errorModal, { size: 'l', backdrop: 'static', keyboard : false });
		}


		this.projectService.getProjectsForAssign().subscribe({
			next: (response: any) => {
				this.projects = response;
				this.loading = false;
			},
			error: (err) => {
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting Projects');
			}
		});

  }

	subscribeSearchText() {
		// Subscribe to search input changes with debounce
		this.searchSubject.pipe(
			debounceTime(300), // Adjust debounce time as needed
			distinctUntilChanged()
		).subscribe(() => {
			this.currentPage = 1; // Reset to first page when search text changes
			if (this.currentStep === 1) {
				this.getUsers();
			}
		});
	}
  
  nextStep(): void {
		this.submitted = true;

		if (this.createForm.valid && this.selectedProject) {
			if (this.currentStep < this.steps.length) {
				this.currentStep++;
				if (this.currentStep === 1) {
					this.getUsers();
				}
				this.submitted = false;
			}
		}
  }

  backStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
			if (this.currentStep === 1) {
				this.getUsers();
			}
    }
  }

	onSubmit() {
		this.submitted = true;
		this.spinner = true;

		if (!this.createForm.valid) {
			this.submitted = false;
			this.spinner = false;
			this.currentStep = 0;
			return;
		}
		
		this.createForm.value.description = this.description;
		this.createForm.value.parent_project_id = this.selectedProject;

		const selectedUserIds: any[] = this.selectedUsers.map(user => user.id);

		this.projectService.createProject({ project: this.createForm.value, client_users: selectedUserIds }).subscribe({
			next: () => {
				this.submitted = false;
				this.spinner = false;
				this.createProjectModalRef.close();
				this.success = true;
				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			},
			error: (err) =>{
				this.success = false;
				this.submitted = false;
				this.spinner = false;
				this.authService.toastErrorMessage(err.message ? err.message : err , 'Project Creation!');
			}
		});

	}

	addRemoveMembers(data: any) {
    if (data.state) {
      this.selectedUsers.push(data)
    } else {
      const index = this.selectedUsers.indexOf(data);
      if (index !== -1) {
        this.selectedUsers.splice(index, 1);
      }
    }
  }

	getUsers() {
		this.loading = true;

    const params = {
      page: this.currentPage.toString(),
      pageSize: this.pageSize.toString(),
      searchText: this.searchText,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
			isList: true
    };
		
		this.userService.getClientUsers(params).subscribe({
			next: (response: any) => {
				this.users = response.users;
				this.users.forEach(user => {
					user.state = this.selectedUsers.some(selectedUser => selectedUser.id === user.id);
				});
				this.totalUsers = response.totalUsers;
				this.calculateIndexes();
				this.loading = false;
			},
			error: (err) => {
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting Users')
			}
		});
  }

  onSearch() {
    this.searchSubject.next(this.searchText);
  }

	onPageChange(page: number) {
    this.currentPage = page;
		this.getUsers();
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

		this.getUsers();
  }

	calculateIndexes() {
    this.startIndex = (this.currentPage - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalUsers);
  }

	async closeModal() {
		if (this.authService.currentClientValue.plan) {
			if (this.router.url.includes('projects')) {
				if (this.success) {
					await this.projectService.getProjects();
				}
			}

			this.createForm.reset();
			this.modalService.dismissAll();
		} else {
			this.modalService.dismissAll();
		}
	}
}
